/**
 * Products Firestore Service
 * Direct Firestore SDK calls for products CRUD operations
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';

// Helper to convert Firestore timestamp to Date
const convertTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.toDate) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
};

// Helper to convert document to product object
const docToProduct = (doc) => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    // Normalize camelCase Firestore fields to snake_case for UI consistency
    stock_count: data.stockCount ?? data.stock_count ?? 0,
    low_stock_threshold: data.lowStockThreshold ?? data.low_stock_threshold ?? 10,
    total_units_sold: data.totalUnitsSold ?? data.total_units_sold ?? 0,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt)
  };
};

/**
 * Get all products with pagination
 */
export const getAll = async (page = 1, pageLimit = 10) => {
  try {
    const productsRef = collection(db, 'products');

    const countSnapshot = await getCountFromServer(productsRef);
    const totalCount = countSnapshot.data().count;

    let q = query(productsRef, orderBy('createdAt', 'desc'), limit(pageLimit));

    if (page > 1) {
      const offset = (page - 1) * pageLimit;
      const prevQuery = query(productsRef, orderBy('createdAt', 'desc'), limit(offset));
      const prevSnapshot = await getDocs(prevQuery);
      const lastDoc = prevSnapshot.docs[prevSnapshot.docs.length - 1];
      if (lastDoc) {
        q = query(productsRef, orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(pageLimit));
      }
    }

    const snapshot = await getDocs(q);
    const products = snapshot.docs.map(docToProduct);

    return {
      products,
      pagination: {
        totalCount,
        totalPages: Math.ceil(totalCount / pageLimit) || 1,
        currentPage: page
      }
    };
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Get product by ID
 */
export const getById = async (id) => {
  try {
    const productRef = doc(db, 'products', id);
    const productDoc = await getDoc(productRef);

    if (!productDoc.exists()) {
      throw new Error('Product not found');
    }

    return docToProduct(productDoc);
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

/**
 * Create a new product
 */
export const create = async (productData) => {
  try {
    const productDoc = {
      name: productData.name || '',
      price: parseFloat(productData.price) || 0,
      cost: parseFloat(productData.cost) || 0,
      sku: productData.sku || '',
      stockCount: parseInt(productData.stock_count || productData.stockCount) || 0,
      lowStockThreshold: parseInt(productData.low_stock_threshold || productData.lowStockThreshold) || 10,
      totalUnitsSold: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const productsRef = collection(db, 'products');
    const docRef = await addDoc(productsRef, productDoc);

    return {
      id: docRef.id,
      ...productDoc,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

/**
 * Update a product
 */
export const update = async (id, productData) => {
  try {
    const productRef = doc(db, 'products', id);

    const updateData = {
      name: productData.name,
      price: parseFloat(productData.price) || 0,
      cost: parseFloat(productData.cost) || 0,
      sku: productData.sku || '',
      stockCount: parseInt(productData.stock_count || productData.stockCount) || 0,
      lowStockThreshold: parseInt(productData.low_stock_threshold || productData.lowStockThreshold) || 10,
      updatedAt: serverTimestamp()
    };

    await updateDoc(productRef, updateData);

    // Return updated document
    return await getById(id);
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

/**
 * Delete a product
 */
export const remove = async (id) => {
  try {
    const productRef = doc(db, 'products', id);
    await deleteDoc(productRef);
    return { id };
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

/**
 * Get total product count
 */
export const getCount = async () => {
  try {
    const productsRef = collection(db, 'products');
    const snapshot = await getCountFromServer(productsRef);
    return snapshot.data().count;
  } catch (error) {
    console.error('Error getting product count:', error);
    throw error;
  }
};

// Export as object for compatibility with API pattern
const productsService = {
  getAll,
  getById,
  create,
  update,
  delete: remove
};

export default productsService;
